import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Camera, Upload, Trash2, Loader2, Check, ImagePlus, X, Star } from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

const LIMIT = 20;

export function PhotoGallery({ currentAvatarUrl, onAvatarChange, getAuthHeaders, compact = false }) {
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [remaining, setRemaining] = useState(LIMIT);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [settingAvatar, setSettingAvatar] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchLibrary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/media/library`, { headers: getAuthHeaders() });
      const result = await safeJson(res);
      if (result.ok && result.data) {
        setPhotos(result.data.photos || []);
        setTotal(result.data.total || 0);
        setRemaining(result.data.remaining ?? LIMIT);
      }
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchLibrary(); }, [fetchLibrary]);

  const handleUpload = async (file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid format. Use JPG, PNG, or WEBP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB.');
      return;
    }
    if (remaining <= 0) {
      toast.error(`Gallery full (${LIMIT} photos). Delete one first.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const res = await fetch(`${API}/media/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });
      const result = await safeJson(res);
      if (result.ok && result.data?.media) {
        toast.success('Photo uploaded!');
        if (result.data.media.is_active_avatar && onAvatarChange) {
          onAvatarChange(result.data.media.url);
        }
        await fetchLibrary();
      } else {
        const detail = result.data?.detail;
        toast.error(typeof detail === 'string' ? detail : detail?.message || result.error || 'Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed. Try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSetAvatar = async (mediaId, url) => {
    setSettingAvatar(mediaId);
    try {
      const res = await fetch(`${API}/media/${mediaId}/set-avatar`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      const result = await safeJson(res);
      if (result.ok) {
        toast.success('Profile picture updated!');
        setPhotos(prev => prev.map(p => ({ ...p, is_active_avatar: p.media_id === mediaId })));
        if (onAvatarChange) onAvatarChange(url);
      } else {
        toast.error(result.error || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update profile picture');
    } finally {
      setSettingAvatar(null);
    }
  };

  const handleDelete = async (mediaId) => {
    setDeletingId(mediaId);
    try {
      const res = await fetch(`${API}/media/${mediaId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const result = await safeJson(res);
      if (result.ok) {
        toast.success('Photo deleted');
        await fetchLibrary();
        const deletedPhoto = photos.find(p => p.media_id === mediaId);
        if (deletedPhoto?.is_active_avatar && onAvatarChange) {
          const nextActive = photos.find(p => p.media_id !== mediaId);
          onAvatarChange(nextActive?.url || null);
        }
      } else {
        toast.error(result.error || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete photo');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const activePhoto = photos.find(p => p.is_active_avatar);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
      </div>
    );
  }

  /* ── Compact mode (Dashboard) ── */
  if (compact) {
    return (
      <Card className="glass-card border-border p-4" data-testid="photo-gallery-compact">
        <div className="flex items-center gap-4 mb-3">
          <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-orange-500/50 flex-shrink-0">
            {activePhoto ? (
              <img src={activePhoto.url} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-muted/30 flex items-center justify-center">
                <Camera className="w-6 h-6 text-muted-foreground/50" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm mb-0.5">Profile Photos</h3>
            <p className="text-xs text-muted-foreground">{total} of {LIMIT} photos</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="gallery-upload-btn"
            disabled={uploading || remaining <= 0}
            onClick={() => fileInputRef.current?.click()}
            className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
            <span className="ml-1.5 text-xs">Add</span>
          </Button>
        </div>

        {photos.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            {photos.slice(0, 8).map(photo => (
              <button
                key={photo.media_id}
                data-testid={`gallery-photo-${photo.media_id}`}
                onClick={() => !photo.is_active_avatar && handleSetAvatar(photo.media_id, photo.url)}
                disabled={settingAvatar === photo.media_id}
                className={`relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all
                  ${photo.is_active_avatar ? 'border-orange-500 ring-1 ring-orange-500/30' : 'border-border hover:border-border'}
                  ${settingAvatar === photo.media_id ? 'opacity-50' : ''}`}
              >
                <img src={photo.thumbnail_url || photo.url} alt="" className="w-full h-full object-cover" />
                {photo.is_active_avatar && (
                  <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-orange-400" />
                  </div>
                )}
                {settingAvatar === photo.media_id && (
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
                    <Loader2 className="w-3 h-3 animate-spin text-white" />
                  </div>
                )}
              </button>
            ))}
            {photos.length > 8 && (
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted/30 flex items-center justify-center text-xs text-muted-foreground/60">
                +{photos.length - 8}
              </div>
            )}
          </div>
        )}

        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); }} />
      </Card>
    );
  }

  /* ── Full mode (Settings) ── */
  return (
    <Card className="glass-card border-border p-6" data-testid="photo-gallery-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-bold uppercase">Photo Gallery</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total} of {LIMIT} photos{remaining <= 3 && remaining > 0 ? ` \u2022 ${remaining} remaining` : ''}
            {remaining <= 0 ? ' \u2022 Gallery full' : ''}
          </p>
        </div>
        <Button
          data-testid="gallery-upload-btn-full"
          disabled={uploading || remaining <= 0}
          onClick={() => fileInputRef.current?.click()}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          size="sm"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
          Upload Photo
        </Button>
      </div>

      {/* Upload drop zone */}
      <div
        data-testid="gallery-dropzone"
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !uploading && remaining > 0 && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all mb-4
          ${dragOver ? 'border-orange-500 bg-orange-500/10' : 'border-border hover:border-border bg-muted/20'}
          ${uploading ? 'pointer-events-none opacity-50' : ''}`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            <p className="text-sm text-muted-foreground">Processing...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <ImagePlus className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Drag & drop or click to upload
            </p>
            <p className="text-xs text-muted-foreground/50">JPG, PNG, WEBP up to 5MB</p>
          </div>
        )}
      </div>

      {/* Gallery Grid */}
      {photos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground/50">
          <Camera className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No photos yet. Upload your first profile photo!</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {photos.map(photo => (
            <div
              key={photo.media_id}
              data-testid={`gallery-item-${photo.media_id}`}
              className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all
                ${photo.is_active_avatar ? 'border-orange-500 ring-2 ring-orange-500/20' : 'border-border hover:border-border'}`}
            >
              <img
                src={photo.thumbnail_url || photo.url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />

              {/* Active badge */}
              {photo.is_active_avatar && (
                <Badge className="absolute top-1.5 left-1.5 bg-orange-600 text-white text-[10px] px-1.5 py-0 h-5 gap-0.5">
                  <Star className="w-2.5 h-2.5" /> Active
                </Badge>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-1">
                {!photo.is_active_avatar && (
                  <Button
                    size="sm"
                    data-testid={`set-avatar-${photo.media_id}`}
                    onClick={() => handleSetAvatar(photo.media_id, photo.url)}
                    disabled={settingAvatar === photo.media_id}
                    className="bg-orange-600 hover:bg-orange-700 text-white h-7 text-xs w-full"
                  >
                    {settingAvatar === photo.media_id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-3 h-3 mr-1" /> Set Active
                      </>
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  data-testid={`delete-photo-${photo.media_id}`}
                  onClick={() => handleDelete(photo.media_id)}
                  disabled={deletingId === photo.media_id}
                  className="h-7 text-xs w-full"
                >
                  {deletingId === photo.media_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); }}
      />
    </Card>
  );
}
